import { useState, useEffect, useCallback } from 'react'
import { ExternalLink } from 'components/layout/links'
import { connect } from 'react-redux'
import { Faq, Container, Text } from 'roadie-ui'
import { taxSelector } from 'selectors'
import { FeatureFlag } from 'Optimizely'
import Panel, { PanelTitle, PanelSpacer } from 'components/layout/Panel'
import Page from 'rui/widgets/Page'
import Form, { Input } from 'components/form'
import DriveSidebar from 'components/layout/DriveSidebar'
import styled, { css } from 'styled-components/macro'
import { body2, body1, overline, h6, caption } from 'styles/mixins/textstyles'
import media from 'styles/mixins/media'
import { fetchTaxSummary } from 'actions/taxes'
import { StaticLoader } from 'components/misc/Loader'
import { formatNum } from 'util/format'
import { SummaryItem } from 'components/pages/GigHistory/GigHistoryPage'
import Loader from 'components/misc/Loader'
import Spacer from 'components/misc/Spacer'
import { buttonComponent } from 'components/layout/buttons'
import range from 'lodash/range'
import { AdaptiveExternalLink } from 'components/layout/links'
import { isWebviewSelector } from 'selectors'
import { api } from 'roadie-ui'
import { useSelector } from 'react-redux'
import Icon from 'components/misc/Icon'
import { Caption } from 'components/layout/text'
import { BusinessInfoForm } from '../account/BasicInfo/BusinessInfo'
import { Link } from 'react-router-dom'
import ButtonLink from 'components/Button/ButtonLink'
import { LocalizedRichText, LocalizedText, useLocalization } from 'localization'

const Faqs = styled.div`
  margin: 0;
  padding: 1.6rem 2.4rem;
`

const FaqSpacer = styled(Spacer).attrs({ height: '2.4rem' })``

const Notice = styled.p`
  ${(props) => body2(props.theme.colors.black, 'medium')}
  text-align: left;
  margin-left: 11.5rem;
  position: relative;

  &:before {
    content: "NOTICE";
    ${(props) => overline(props.theme.colors.white)}
    font-weight: 700;
    background-color: ${(props) => props.theme.colors.alertBase};
    border-radius: 4px;
    padding: .2rem 1.2rem;
    position: absolute;
    right: calc(100% + 3.2rem);
  }

  ${media.mobile`
    margin-left: 0;
    margin-top: 50px;

    &:before {
      right: initial;
      bottom: calc(100% + 20px);
    }
  `}
  }
`

const SummaryTimeframe = styled(Form)`
  display: flex;
  align-items: center;
  justify-content: center;

  & > :first-child {
    min-width: 10rem;
  }
`

const SummaryLabel = styled.label`
  ${(props) => body1(props.theme.colors.black, 'medium')}
  margin-left: 1.2rem;
`

const TaxSummaryItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2.4rem 0;

  & > * {
    padding: 0 3rem;
  }

  & > :not(:first-child) {
    border-left: 1px solid ${(props) => props.theme.colors.greyLight};
  }

  ${media.mobile`
    & > :not(:first-child) {
      border-left: 1px solid ${(props) => props.theme.colors.greyLight};
    }

    & > * > div:first-child {
      font-size: 2.6rem;
    }

    & > * :nth-child(2) > div {
      font-size: 1.3rem;
    }
    
    & > * {
      padding: 0 1.7rem;
    }

    & > * {
      padding: 0 1.7rem;
    }
  `}
`

const DownloadItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem 2.4rem;
  height: 9rem;
  border-top: 1px solid ${(props) => props.theme.colors.greyLight};

  ${media.mobile`
    padding: 2rem 1rem;
    height: auto;
  `}
`

const DownloadInfo = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
`

const DownloadYear = styled.div`
  flex-grow: 0;
  flex-shrink: 0;

  text-align: center;
  color: ${(props) => props.theme.colors.greyDark};
  text-transform: uppercase;
  margin-right: 2.4rem;
  flex-shrink: 0;

  & > :nth-child(1) {
    ${(props) => caption(props.theme.colors.black, 'medium')}
  }

  & > :nth-child(2) {
    ${(props) => h6(props.theme.colors.black)}
  }

  & > :nth-child(3) {
    ${(props) => caption(props.theme.colors.black, 'medium')}
  }

  ${media.mobile`
    & > :nth-child(2) {
      font-size: 1.8rem;
    }
  `}
`

const DownloadDetails = styled.div`
  flex-grow: 1;
  flex-shrink: 1;

  text-align: left;
  margin-right: auto;
  overflow: hidden;

  & > :first-child {
    ${(props) => h6(props.theme.colors.black)}
  }

  & > :last-child {
    ${(props) => caption(props.theme.colors.black, 'medium')}
  }
`

const DownloadAction = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  margin-left: 1.6rem;

  ${media.mobile`
    text-align: center;
    margin-right: 2.4rem;
  `}
`

const DownloadButton = styled(buttonComponent('button', 'primary'))`
  transition:
    all 150ms ease,
    padding 0ms;

  ${(props) =>
    props.loading &&
    css`
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    `}

  ${(props) =>
    props.disabled &&
    css`
      cursor: auto;
    `}

  width: 20rem;
`

const DownloadLoader = styled(Loader).attrs({ background: 'none', size: 38 })`
  ${(props) =>
    props.visible &&
    css`
      background: ${props.theme.colors.primaryBase};
    `}
`

const TaxForm = styled(Form)`
  width: 24rem;
  margin: 2.4rem auto;
`

const YearSelect = styled(Input.Select)`
  width: 8rem;
`

const CheckIfWebview = connect(isWebviewSelector)((props) => {
  const { isWebview, handleDownload, loading, disabled, docType, fileType, year } = props

  const { mediaSize } = useSelector((state) => ({
    mediaSize: state.config.mediaSize,
  }))

  return isWebview ? (
    <AdaptiveExternalLink id="download-mileage" to={`download/${fileType}/${year}`} disabled={disabled}>
      {loading ? (
        <DownloadLoader visible />
      ) : (
        <Icon disabled={disabled} symbol={`external-link-${disabled ? 'disabled' : 'active'}`} />
      )}
    </AdaptiveExternalLink>
  ) : mediaSize === 'mobile' ? (
    loading ? (
      <DownloadLoader visible />
    ) : (
      <Icon
        disabled={disabled}
        symbol={`arrow-down-${disabled ? 'disabled' : 'active'}`}
        onClick={disabled ? null : handleDownload}
      />
    )
  ) : (
    <DownloadButton
      id="download-mileage"
      loading={loading}
      onClick={disabled ? null : handleDownload}
      disabled={disabled}
    >
      {loading ? (
        <DownloadLoader />
      ) : mediaSize === 'mobile' ? (
        <Icon
          disabled={disabled}
          symbol={`arrow-down-${disabled ? 'disabled' : 'active'}`}
          onClick={disabled ? null : handleDownload}
        />
      ) : (
        `Download ${docType}`
      )}
    </DownloadButton>
  )
})

const SupportLinkWrapper = styled.div`
  padding: 1.6rem 3.2rem;
  border-top: 1px solid ${(props) => props.theme.colors.greyLight};
`

const LAST_YEAR = new Date().getFullYear() - 1

const TaxInfoPage = (props) => {
  const { t } = useLocalization()
  const { dispatch, taxes, profile } = props
  const { summary, downloads } = taxes

  const [loadingSummary, setLoadingSummary] = useState(false)
  const [year, setYear] = useState(LAST_YEAR)
  const [years, setYears] = useState([])
  const [downloading1099, setDownloading1099] = useState(false)
  const [downloadingMileage, setDownloadingMileage] = useState(false)
  const latestEINData = profile.ein_verifications.at(-1)

  const has1099 = downloads && !!downloads.federal_1099
  const subtitleFor1099 = has1099
    ? t('dweb_tax_info_available_download')
    : (summary && summary.total_earnings) > 600
      ? t('dweb_tax_info_form_not_available_yet')
      : t('dweb_tax_info_not_enough_earnings_for_1099')

  const loadSummary = useCallback(
    (year) => {
      setLoadingSummary(true)
      dispatch(fetchTaxSummary(year)).then(() => {
        setLoadingSummary(false)
      })
    },
    [dispatch]
  )

  const handleDownload1099 = () => {
    setDownloading1099(true)
    api
      .downloadFile({
        path: taxes.downloads.federal_1099,
        name: `${year}_Roadie_1099.pdf`,
      })
      .then(() => {
        setDownloading1099(false)
      })
  }

  const handleDownloadMileage = () => {
    setDownloadingMileage(true)
    api
      .downloadFile({
        path: taxes.downloads.mileage,
        name: `${year}_Roadie_Mileage_Log.csv`,
      })
      .then(() => {
        setDownloadingMileage(false)
      })
  }

  const handleYear = (event) => {
    const year = event.target.value
    setYear(year, () => loadSummary(year))
  }

  const getTaxYears = useCallback(() => {
    const start = Math.max(2018, new Date(profile.member_since).getFullYear())
    const end = new Date().getFullYear()
    const years = range(start, end)
    setYears(years)
  }, [profile])

  useEffect(() => {
    loadSummary(year)
    getTaxYears()
  }, [loadSummary, year, getTaxYears])

  return (
    <Page sidebar={<DriveSidebar />}>
      {years.length != 0 ? (
        <>
          <Panel>
            <Notice>
              <LocalizedText translationKey="dweb_tax_info_not_tax_form_roadie_does_not_provide_tax" />
            </Notice>
          </Panel>

          <PanelSpacer />

          <PanelTitle>
            <LocalizedText translationKey="dweb_tax_info_year_tax_summary" params={{ year }} />
          </PanelTitle>
          <Panel noPadding>
            <TaxForm>
              <SummaryTimeframe>
                <YearSelect id="tax-year-select" onChange={handleYear} key={year} defaultValue={year}>
                  {years.reverse().map((year) => (
                    <option key={year}>{year}</option>
                  ))}
                </YearSelect>
                <SummaryLabel htmlFor="tax-year-select">
                  <LocalizedText translationKey="dweb_tax_info_tax_summary" />
                </SummaryLabel>
              </SummaryTimeframe>
            </TaxForm>
            {summary && summary.completed_gigs && summary.miles_driven && summary.total_earnings ? (
              <>
                <TaxSummaryItems>
                  <SummaryItem
                    isCurrency
                    value={formatNum(summary.total_earnings, { maxLength: 6 })}
                    title={<LocalizedText translationKey="dweb_common_total_earnings" />}
                    id="total-earnings"
                    tooltip="Total payout this year minus parking and tolls."
                  />
                  <SummaryItem
                    value={formatNum(summary.completed_gigs, { maxLength: 6 })}
                    title={<LocalizedText translationKey="dweb_tax_info_deliveries_completed" />}
                    id="completes-gigs"
                    tooltip="The number of Gigs you received payment for completing, including Gigs that you attempted to deliver and ones that were cancelled."
                  />
                  <FeatureFlag feature="drive_show_miles_driven">
                    {(enabled) =>
                      enabled && (
                        <SummaryItem
                          value={formatNum(summary.miles_driven, { maxLength: 6 })}
                          title={<LocalizedText translationKey="dweb_tax_info_miles_driven" />}
                          id="miles-driven"
                          tooltip="The mileage from the pickup address to the delivery address. It may differ from the mileage you drove."
                        />
                      )
                    }
                  </FeatureFlag>
                </TaxSummaryItems>

                <DownloadItem>
                  <DownloadInfo>
                    <DownloadYear>
                      <div>
                        <LocalizedText translationKey="dweb_tax_info_year" />
                      </div>
                      <div>{year}</div>
                      <div>
                        <LocalizedText translationKey="dweb_tax_info_federal" />
                      </div>
                    </DownloadYear>
                    <DownloadDetails>
                      <div>
                        <LocalizedText
                          translationKey="dweb_tax_info_form_1099"
                          params={{ formType: year > 2019 ? 'NEC' : 'MISC' }}
                        />
                      </div>
                      <div>{subtitleFor1099}</div>
                    </DownloadDetails>
                  </DownloadInfo>
                  <DownloadAction>
                    <CheckIfWebview
                      disabled={!has1099}
                      loading={downloading1099}
                      handleDownload={handleDownload1099}
                      docType="Form"
                      fileType="1099"
                      year={year}
                    />
                  </DownloadAction>
                </DownloadItem>

                <DownloadItem>
                  <DownloadInfo>
                    <DownloadYear>
                      <div>
                        <LocalizedText translationKey="dweb_tax_info_year" />
                      </div>
                      <div>{year}</div>
                      <div>
                        <LocalizedText translationKey="dweb_tax_info_mile_log" />
                      </div>
                    </DownloadYear>
                    <DownloadDetails>
                      <div>
                        <LocalizedText translationKey="dweb_tax_info_roadie_mileage_log" />
                      </div>
                      <div>
                        <LocalizedText translationKey="dweb_tax_info_available_download" />
                      </div>
                    </DownloadDetails>
                  </DownloadInfo>
                  <DownloadAction>
                    <CheckIfWebview
                      loading={downloadingMileage}
                      handleDownload={handleDownloadMileage}
                      docType="CSV"
                      fileType="mileage"
                      year={year}
                    />
                  </DownloadAction>
                </DownloadItem>
              </>
            ) : (
              <Container.Base $padding="0 2rem 2rem 2rem">
                <Text.Body2 $color="neutral-dark">
                  <LocalizedText translationKey="dweb_tax_info_no_information_report_year_please_select_another_year" />
                </Text.Body2>
              </Container.Base>
            )}
          </Panel>
        </>
      ) : loadingSummary ? null : (
        <Panel>
          <LocalizedText translationKey="dweb_tax_info_do_not_have_any_tax_information_available_right" />
        </Panel>
      )}

      <PanelSpacer />

      <PanelSpacer />

      <div>
        <PanelTitle>
          <LocalizedText translationKey="dweb_tax_info_business_information" />
        </PanelTitle>
        <Panel>
          {profile.ein_verifications.length == 0 ? (
            <Container.Grid $gridTemplateColumns="2fr 1fr" $gridGap="2rem">
              <Container.GridItem $textAlign="start">
                <Text.Body2 $color="neutral-black">
                  <LocalizedText translationKey="dweb_tax_info_no_business_information_file_if_youd_like_1099" />
                </Text.Body2>
              </Container.GridItem>
              <Container.GridItem $justifySelf="end" $alignSelf="center">
                <ButtonLink variant="secondary" id="basic-info-link" to={'/account/basic-info'} $padding="10px 30px">
                  <LocalizedText translationKey="dweb_tax_info_add_business" />
                </ButtonLink>
              </Container.GridItem>
            </Container.Grid>
          ) : (
            <div>
              <BusinessInfoForm einVerification={latestEINData} page="tax-info" />
            </div>
          )}
        </Panel>
      </div>

      <PanelSpacer />
      <PanelTitle>
        <LocalizedText translationKey="dweb_dashboard_account_status_faqs" />
      </PanelTitle>
      <Panel noPadding>
        <Faqs>
          <Faq title={<LocalizedText translationKey="dweb_tax_info_will_i_get_1099_from_roadie" />}>
            <LocalizedText translationKey="dweb_tax_info_1099_tax_forms_based_how_much_earned_roadie" />
            <br />
            <br />
            <LocalizedText translationKey="dweb_tax_info_if_earned_at_least_600_total_roadie_app" />
            <br />
            <br />
            <LocalizedText translationKey="dweb_tax_info_if_earned_less_than_600_roadie_app_will" />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_how_will_i_receive_my_1099" />}>
            <LocalizedText translationKey="dweb_tax_info_roadie_will_mail_form_1099_preferred_address_listed" />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_what_do_i_do_if_i_dont_get" />}>
            <LocalizedRichText
              translationKey="dweb_tax_info_no_1099_available_contact_support"
              params={{
                supportEmailLink: (
                  <ExternalLink href={`mailto:${import.meta.env.REACT_APP_SUPPORT_EMAIL}`}>
                    <LocalizedText translationKey="dweb_tax_info_support_roadie_com" />
                  </ExternalLink>
                ),
              }}
            />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_if_im_not_going_receive_1099_do_i" />}>
            <LocalizedText translationKey="dweb_tax_info_yes_even_if_dont_receive_1099_youll_still" />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_can_i_deduct_my_mileage_other_expenses" />}>
            <LocalizedText translationKey="dweb_tax_info_we_not_tax_experts_we_advise_speak_tax" />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_how_my_mileage_log_calculated" />}>
            <LocalizedText translationKey="dweb_tax_info_roadies_mileage_log_shows_mileage_from_pickup_drop" />
          </Faq>
          <FaqSpacer />
          <Faq
            title={
              <LocalizedText translationKey="dweb_tax_info_will_i_need_roadies_ein_employer_identification_number" />
            }
          >
            <LocalizedText translationKey="dweb_tax_info_roadie_drivers_not_employees_so_do_not_need" />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_how_my_total_earnings_calculated" />}>
            <LocalizedText translationKey="dweb_tax_info_we_calculate_total_earnings_tax_year_by_subtracting" />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_can_i_get_1099_form_issued_my_business" />}>
            <LocalizedRichText
              translationKey="dweb_tax_info_issue_1099_to_business"
              params={{
                updateProfileLink: (
                  <Link to={'/account/basic-info'}>
                    <LocalizedText translationKey="dweb_tax_info_update_driver_profile" />
                  </Link>
                ),
              }}
            />
          </Faq>
          <FaqSpacer />
          <Faq title={<LocalizedText translationKey="dweb_tax_info_how_do_i_find_my_ein_if_i" />}>
            <LocalizedText translationKey="dweb_tax_info_employer_identification_number_ein_was_provided_by_irs" />
          </Faq>
        </Faqs>
        <SupportLinkWrapper>
          <Caption align="center" emphasis="medium" fontSize="1.5rem">
            <LocalizedRichText
              translationKey="dweb_tax_info_more_questions_visit_link1"
              params={{
                link1: (
                  <ExternalLink href={import.meta.env.REACT_APP_SUPPORT_URL}>
                    <LocalizedText translationKey="dweb_tax_info_support_center" />
                  </ExternalLink>
                ),
              }}
            />
          </Caption>
        </SupportLinkWrapper>
      </Panel>
      {loadingSummary && <StaticLoader />}
    </Page>
  )
}

export default connect(taxSelector)(TaxInfoPage)
